import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography, Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
    container: {
        padding: theme.spacing(15, 37.5),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(12),
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        opacity: 1,
        background: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)),
                  url('https://assets.languify.in/images/b2cFrame.png') `,
        [theme.breakpoints.down('xl')]: {
            padding: theme.spacing(15, 15),
        },
        [theme.breakpoints.down('lg')]: {
            padding: theme.spacing(15),
        },
        [theme.breakpoints.down('md')]: {
            padding: theme.spacing(7, 10),
            gap: theme.spacing(7),
            // The header is sticky below md; keep #faqs from landing under it.
            scrollMarginTop: '80px',
        },
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(7, 5),
        },
    },
    containerVisible: {
        opacity: 1,
        transform: 'translateY(0)',
    },
    typo: {
        textAlign: 'center',
        ...theme.typography['h2-bold'],
    },
    accordion: {
        border: '2px solid',
        borderColor: theme.palette.neutral['clr-300'],
        borderRadius: '8px',
        boxShadow: 'none',
        '&:before': {
            display: 'none',
        },
        overflow: 'hidden',
        marginBottom: theme.spacing(5),
        [theme.breakpoints.down('md')]: {
            marginBottom: theme.spacing(3),
        },
    },
    accordionExpanded: {
        marginBottom: '20px',
        [theme.breakpoints.down('md')]: {
            marginBottom: theme.spacing(3),
        },
    },
    accordionSummary: {
        backgroundColor: theme.palette.background.default,
        height: '68px',
        display: 'flex',
        alignItems: 'center',
        borderRadius: '4px 4px 0 0',
        padding: theme.spacing(0, 4),
        [theme.breakpoints.down('md')]: {
            height: 'auto'
        },
        '& .MuiAccordionSummary-expandIconWrapper': {
            width: '36px',
            height: '36px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'transform 0.5s ease-in-out',
        },
        '& .MuiSvgIcon-root': {
            fontSize: '36px',
            transition: 'transform 0.5s ease-in-out',
        },
    },
    accordionSummaryExpanded: {
        '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'rotateX(180deg)',
        },
    },
    accordionDetails: {
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        borderTop: 'none',
        paddingTop: 0,
        paddingBottom: theme.spacing(4),
        paddingLeft: theme.spacing(10),
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
    },
    typography: {
        ...theme.typography['h6-medium'],
        color: "#2D3142",
    },
    typographyVisible: {
        opacity: 1,
        transform: 'translateY(0)',
    },
}));

const faqs = [
    {
        question: `Is this really better than practicing with peers?`,
        answer: `Peers are great for collaborative practice, but they can't always provide consistent, structured feedback or objectively evaluate your performance. Case Master AI simulates a real case interview, evaluates your performance across 6+ key parameters, and provides detailed, actionable feedback after every interview. Available 24/7, it lets you practice whenever it's convenient for you and improve with every case.`,
    },
    {
        question: `I'm a complete beginner. Will this work for me?`,
        answer: `Absolutely. Case Master AI is designed for both beginners and experienced candidates. Start with foundational case types like profitability, market sizing, and market entry, then progress to more advanced interview-style cases as your skills improve.`,
    },
    {
        question: `How is AI feedback better than a human coach?`,
        answer: `Human coaches are incredibly valuable, but they can't review every practice session. Case Master AI provides instant, detailed feedback after every interview, highlighting your strengths, weaknesses, and personalized improvement tips. Every session is also saved with a complete interview transcript, making it easy to revisit your responses, compare past performances, and measure your progress over time.`,
    },
    {
        question: `Can I practice firm-specific cases (McKinsey, BCG, Bain)?`,
        answer: `Yes. Our case library includes MBB-style and consulting interview cases across multiple industries and problem types, including profitability, market entry, pricing, operations, growth strategy, and more. This helps you prepare for the interview styles used by leading consulting firms.`,
    },
    {
        question: `Is Case Master AI only for consulting interviews, or also tech and product roles?`,
        answer: `Case Master AI supports consulting, product management, strategy, business, and other case-based interviews. Whether you're preparing for consulting firms or product and strategy roles, you'll find relevant case interviews to practice with.`,
    },
    {
        question: `How many cases should I practice to be interview-ready?`,
        answer: `Most successful candidates solve around 40–60 quality cases before interviews. With Case Master AI, you can practice much faster because you receive immediate feedback, interview transcripts, and improvement suggestions after every case helping you learn from each attempt instead of simply completing more cases.`,
    },
    {
        question: `Do I need to schedule practice sessions?`,
        answer: `No. Practice whenever it's convenient for you between classes, after work, or late at night. Case Master AI is available 24/7, so you can fit interview preparation into your schedule and practice at your own pace.`,
    },
    {
        question: `What if I don't like it? Is there a refund?`,
        answer: `You can try Case Master AI for free before making a purchase. Complete one full case interview, receive detailed AI feedback, and explore the platform to see how it works before deciding to upgrade.`,
    },
];

export default function CaseFaq() {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const sectionRef = useRef(null);

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    return (
        <Box
            className={`${classes.container}`}
            ref={sectionRef}
            id='faqs'
        >
            <Typography className={classes.typo} color='b2c.dark-900'>
                Frequently Asked Questions
            </Typography>
            <Box>
                {faqs.map((faq, index) => (
                    <Accordion
                        className={`${classes.accordion} ${expanded === index ?
                            classes.accordionExpanded : ''}`}
                        key={index}
                        expanded={expanded === index}
                        onChange={handleChange(index)}
                        TransitionProps={{ timeout: 500 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                            className={`${classes.accordionSummary} ${expanded === index ?
                                classes.accordionSummaryExpanded : ''}`}
                        >
                            <Typography
                                variant='h6-bold'
                                color='b2c.dark-900'
                                className={`${classes.summaryTypography} ${expanded === index ?
                                    'expanded' : 'collapsed'}`}
                            >
                                {index + 1}.&nbsp;
                            </Typography>
                            <Typography
                                variant='h6-bold'
                                color='b2c.dark-900'
                                className={`${classes.summaryTypography} ${expanded === index ?
                                    'expanded' : 'collapsed'}`}
                            >
                                {faq.question}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={classes.accordionDetails}>
                            <Typography className={`${classes.typography} ${expanded === index ?
                                classes.typographyVisible : ''}`}>
                                {faq.answer}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
    );
}
